import { Component, forwardRef, Input, OnInit, computed, signal, effect } from '@angular/core';
import { InterestService } from '../../shared/services/interest.service';
import { catchError } from 'rxjs/operators';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { some } from 'lodash-es';
import { faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { ModalService } from '../../shared/services/modal.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderComponent } from '../loader/loader.component';
import { CommonModule } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TagService } from '../../api/services/tag.service';
import { Tag } from '../../api/models/tag';

const noop = () => {};

export const SELECTOR_AREA_OF_INTERESTS_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SelectorAreaofinterestsComponent),
  multi: true,
};

@Component({
  selector: 'app-selector-areaofinterests',
  templateUrl: './selector-areaofinterests.component.html',
  styleUrls: ['./selector-areaofinterests.component.scss'],
  providers: [SELECTOR_AREA_OF_INTERESTS_INPUT_CONTROL_VALUE_ACCESSOR],
  standalone: true,
  imports: [
    FontAwesomeModule,
    TranslateModule,
    LoaderComponent,
    FormsModule,
    CommonModule,
    NgbTooltipModule,
  ],
})
export class SelectorAreaofinterestsComponent
  implements OnInit, ControlValueAccessor
{
  // Icons
  faTimes = faTimes;
  faInfoCircle = faInfoCircle;

  @Input() data: any[] = [];
  @Input() maxAmount = <null | number>null;

  searchTerm = '';
  useSearch = false;

  isLoading = signal(false);
  dataSignal = signal<any[]>([]);

  availableTags = signal<Tag[]>([]);
  activeFilterTags = signal<Set<string>>(new Set());

  selectableAreaOfInterests = computed(() => {
    const data = this.dataSignal();
    if (data && data.length > 0) {
      return this.interestService.sort([...data]);
    }
    return [];
  });

  filteredAreaOfInterests = computed(() => {
    const all = this.selectableAreaOfInterests();
    const activeTags = this.activeFilterTags();
    if (activeTags.size === 0) {
      return all;
    }
    return all.filter((interest: any) =>
      interest.tags?.some((tag: string) => activeTags.has(tag))
    );
  });

  tagCounts = computed(() => {
    const all = this.selectableAreaOfInterests();
    const counts = new Map<string, number>();
    for (const tag of this.availableTags()) {
      const count = all.filter((interest: any) =>
        interest.tags?.includes(tag.name)
      ).length;
      counts.set(tag.name!, count);
    }
    return counts;
  });

  /**
   * control accessors
   */

  // The internal data model
  private innerValue: any = [];

  // Placeholders for the callbacks which are later provided
  // by the Control Value Accessor
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  /**
   * Constructor
   *
   * @param interestService
   */
  constructor(
    public interestService: InterestService,
    private modal: ModalService,
    private tagService: TagService
  ) {
    // React to data input changes
    effect(() => {
      const inputData = this.data;
      if (inputData && inputData.length > 0) {
        this.dataSignal.set(inputData);
        this.isLoading.set(false);
      } else if (this.dataSignal().length === 0) {
        this.loadAreaOfInterests();
      }
    });
  }

  // get accessor
  get value(): any {
    return this.innerValue;
  }

  // set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }

  /**
   * Ng on init
   */
  ngOnInit(): void {
    // Initial data setup is handled by the effect in constructor
    this.loadTags();
  }

  loadTags() {
    this.tagService.getTags().subscribe((result) => {
      if (result?.data) {
        this.availableTags.set(result.data);
      }
    });
  }

  toggleFilterTag(tagName: string) {
    const current = new Set(this.activeFilterTags());
    if (current.has(tagName)) {
      current.delete(tagName);
    } else {
      current.add(tagName);
    }
    this.activeFilterTags.set(current);
  }

  /**
   * Get all area of interest data
   */
  loadAreaOfInterests() {
    this.isLoading.set(true);
    this.interestService
      .apiGetAreaOfInterests()
      .pipe(
        catchError(
          this.interestService.handleError('apiGetAreaOfInterests', false)
        )
      )
      .subscribe((result: any) => {
        this.isLoading.set(false);

        if (result !== false) {
          this.dataSignal.set(result.data);
        }
      });
  }

  /**
   * Toggle area of interest value
   *
   * @param value
   */
  toggle(item: any) {
    const exists = some(this.value, (a: any) => a.id === item.id);

    if (exists) {
      this.innerValue = this.value.filter((a: any) => a.id !== item.id);
    } else {
      // Do not exceed maximum amount if given
      if (typeof this.maxAmount == 'number') {
        if (this.value.length < this.maxAmount) {
          this.innerValue = [...this.value, item];
        } else {
          // Show message if maximum is exceeded
          this.modal.Alert(
            'Error',
            'You exceeded the maximum amount of options',
            {},
            [],
            {},
            false
          );
          return;
        }
      } else {
        this.innerValue = [...this.value, item];
      }
    }

    this.onChangeCallback(this.innerValue);
  }

  /**
   * Check if area of interest is already listed
   *
   * @param item
   * @return boolean
   */
  exists(item: any) {
    return some(this.value, (a: any) => a.id === item.id);
  }

  /**
   * Apply search method
   */
  applySearchFilter() {
    if (this.searchTerm == '') this.useSearch = false;
    else this.useSearch = true;
  }

  /**
   * Check if interest contains search term
   *
   * @param array interest
   */
  containsSearchTerm(interest: any) {
    const $nameSelector = 'name';
    if (this.searchTerm == '') return true;

    return interest[$nameSelector]
      .toLowerCase()
      .includes(this.searchTerm.toLowerCase());
  }

  /**
   * Reset search query
   */
  resetSearch() {
    this.searchTerm = '';
    this.useSearch = false;
  }

  // Set touched on blur
  onBlur() {
    this.onTouchedCallback();
  }

  //From ControlValueAccessor interface
  writeValue(value: any) {
    this.innerValue = value || [];
  }

  //From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  //From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }
}
